import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardList,
  Plus,
  Save,
  History,
  Search,
  Download,
  RotateCcw,
  X,
  Clock,
  Users,
  User,
  FileText,
  Calendar,
  ChevronRight,
  Trash2,
  Radio,
  Eye,
  Pencil,
  CheckCircle
} from "lucide-react";

// Sort units numerically (extract numbers from names)
const sortUnitsNumerically = (units) => {
  if (!units) return [];
  return [...units].sort((a, b) => {
    const numA = parseInt((a.match(/\d+/) || [0])[0]);
    const numB = parseInt((b.match(/\d+/) || [0])[0]);
    if (numA !== numB) return numA - numB;
    return a.localeCompare(b);
  });
};

// Convert a Date to NY timezone datetime-local input value
const toLocalDateTimeString = (d) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(d);
  const get = (type) => parts.find(p => p.type === type).value;
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

export default function RollCall() {
  const [activeTab, setActiveTab] = useState("rollcall");
  const [loading, setLoading] = useState(true);

  // Active Roll Call state
  const [activeRollCall, setActiveRollCall] = useState(null);
  const [parsha, setParsha] = useState("");
  const [notes, setNotes] = useState("");
  const [rollCallDate, setRollCallDate] = useState("");
  const [dispatcherUnits, setDispatcherUnits] = useState([]);

  // Units state (for assignment)
  const [availableUnits, setAvailableUnits] = useState([]);
  const [availableDispatchers, setAvailableDispatchers] = useState([]);
  const [unitSearch, setUnitSearch] = useState("");
  const [selectedDispatcher, setSelectedDispatcher] = useState("");
  const unitSearchInputRef = useRef(null);

  // History state
  const [historyList, setHistoryList] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState("");
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");
  const [historyParshaFilter, setHistoryParshaFilter] = useState("");
  const [historyDispatcherFilter, setHistoryDispatcherFilter] = useState("");
  const [historyDispatchersList, setHistoryDispatchersList] = useState([]);
  const [historyOffset, setHistoryOffset] = useState(0);

  // Unit history view state
  const [unitsList, setUnitsList] = useState([]);
  const [selectedUnitView, setSelectedUnitView] = useState(null);
  const [unitRollCalls, setUnitRollCalls] = useState([]);
  const [unitRollCallsTotal, setUnitRollCallsTotal] = useState(0);
  const [showUnitHistory, setShowUnitHistory] = useState(false);

  const [editingDate, setEditingDate] = useState(false);

  // Import units state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);

  // Dialog states
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingRollCall, setViewingRollCall] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(null);

  // Export dialog states
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportType, setExportType] = useState("all"); // "all", "single", "unit", "master"
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exportUnitName, setExportUnitName] = useState("");
  const [exportRollCallId, setExportRollCallId] = useState("");
  const [masterExportType, setMasterExportType] = useState("detailed"); // "detailed" or "summary"

  // Fetch units for assignment
  const fetchUnits = useCallback(async () => {
    try {
      const [unitsRes, dispatchersRes] = await Promise.all([
        axios.get(`${API}/units`, { withCredentials: true }),
        axios.get(`${API}/units/dispatchers`, { withCredentials: true })
      ]);
      setAvailableUnits(unitsRes.data);
      setAvailableDispatchers(dispatchersRes.data);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  }, []);

  // Fetch active roll call
  const fetchActiveRollCall = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rollcall/active`, { withCredentials: true });
      if (res.data) {
        setActiveRollCall(res.data);
        setParsha(res.data.parsha || "");
        setNotes(res.data.notes || "");
        setDispatcherUnits(res.data.dispatcher_units || []);
        const dt = new Date(res.data.roll_call_date || res.data.created_at);
        setRollCallDate(toLocalDateTimeString(dt));
      } else {
        setActiveRollCall(null);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error fetching active roll call:", error);
      }
    }
  }, []);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (historySearch) params.append("search", historySearch);
      if (historyStartDate) params.append("start_date", historyStartDate);
      if (historyEndDate) params.append("end_date", historyEndDate);
      if (historyParshaFilter) params.append("parsha", historyParshaFilter);
      if (historyDispatcherFilter) params.append("dispatcher", historyDispatcherFilter);
      params.append("limit", "50");
      params.append("offset", historyOffset.toString());

      const res = await axios.get(`${API}/rollcall/history/list?${params}`, { withCredentials: true });
      setHistoryList(res.data.items);
      setHistoryTotal(res.data.total);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, [historySearch, historyStartDate, historyEndDate, historyParshaFilter, historyDispatcherFilter, historyOffset]);

  // Fetch dispatchers list for history filter
  const fetchHistoryDispatchers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rollcall/dispatchers/list`, { withCredentials: true });
      setHistoryDispatchersList(res.data);
    } catch (error) {
      console.error("Error fetching dispatchers list:", error);
    }
  }, []);

  // Fetch units list for history
  const fetchUnitsList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rollcall/units/list`, { withCredentials: true });
      setUnitsList(res.data);
    } catch (error) {
      console.error("Error fetching units list:", error);
    }
  }, []);

  // Fetch unit's roll call history
  const fetchUnitRollCalls = useCallback(async (unitName) => {
    try {
      const res = await axios.get(`${API}/rollcall/units/${encodeURIComponent(unitName)}/history`, { withCredentials: true });
      setUnitRollCalls(res.data.items);
      setUnitRollCallsTotal(res.data.total_all_time);
    } catch (error) {
      console.error("Error fetching unit roll calls:", error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchUnits(), fetchActiveRollCall()]);
      setLoading(false);
    };
    init();
  }, [fetchUnits, fetchActiveRollCall]);

  // Fetch history when tab changes or filters change
  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
      fetchUnitsList();
      fetchHistoryDispatchers();
    }
  }, [activeTab, fetchHistory, fetchUnitsList, fetchHistoryDispatchers]);

  // Create new roll call
  const handleCreateRollCall = async () => {
    try {
      const res = await axios.post(`${API}/rollcall`, { parsha, notes }, { withCredentials: true });
      setActiveRollCall(res.data);
      // Initialize editable date from the newly created roll call
      const dt = new Date(res.data.roll_call_date || res.data.created_at);
      setRollCallDate(toLocalDateTimeString(dt));
      toast.success("Roll call created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create roll call");
    }
  };

  // Update roll call
  const handleUpdateRollCall = async () => {
    if (!activeRollCall) return;
    try {
      const res = await axios.put(`${API}/rollcall/${activeRollCall.rollcall_id}`, {
        parsha: parsha,
        notes: notes,
        dispatcher_units: dispatcherUnits,
        roll_call_date: rollCallDate ? new Date(rollCallDate).toISOString() : undefined
      }, { withCredentials: true });
      setActiveRollCall(res.data);
      toast.success("Roll call saved");
    } catch (error) {
      toast.error("Failed to save roll call");
    }
  };

  // Assign unit
  const handleAssignUnit = async (unitName) => {
    if (!activeRollCall || !unitName) return;
    try {
      const res = await axios.post(`${API}/rollcall/${activeRollCall.rollcall_id}/assign`, {
        unit_name: unitName
      }, { withCredentials: true });
      setActiveRollCall(res.data);
      setUnitSearch("");
      toast.success(`${unitName} assigned`);
      // Refocus the unit search input to keep keyboard open
      setTimeout(() => {
        unitSearchInputRef.current?.focus();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign unit");
    }
  };

  // Unassign unit
  const handleUnassignUnit = async (unitName) => {
    if (!activeRollCall) return;
    try {
      const res = await axios.post(`${API}/rollcall/${activeRollCall.rollcall_id}/unassign`, {
        unit_name: unitName
      }, { withCredentials: true });
      setActiveRollCall(res.data);
      toast.success(`${unitName} removed`);
    } catch (error) {
      toast.error("Failed to remove unit");
    }
  };

  // Bulk import units
  const handleBulkImport = async () => {
    if (!activeRollCall || !importText.trim()) return;
    const unitNames = importText
      .split(/[\n,]+/)
      .map(s => s.trim())
      .filter(Boolean);
    if (unitNames.length === 0) return;

    setImporting(true);
    try {
      const res = await axios.post(`${API}/rollcall/${activeRollCall.rollcall_id}/bulk-assign`, {
        unit_names: unitNames
      }, { withCredentials: true });
      setActiveRollCall(res.data);
      const added = res.data.added || 0;
      const skipped = res.data.skipped || 0;
      const unknown = res.data.unknown || 0;
      const unknownNames = res.data.unknown_names || [];
      let msg = `${added} unit${added !== 1 ? "s" : ""} assigned`;
      if (skipped > 0) msg += `, ${skipped} already assigned`;
      if (unknown > 0) msg += `, ${unknown} not recognized`;
      if (added > 0) toast.success(msg);
      else toast.info(msg);
      if (unknown > 0) {
        toast.warning(`Skipped unrecognized: ${unknownNames.join(", ")}`, { duration: 8000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import units");
    } finally {
      setImporting(false);
      setShowImportDialog(false);
      setImportText("");
    }
  };

  // Add dispatcher
  const handleAddDispatcher = () => {
    if (!selectedDispatcher || dispatcherUnits.includes(selectedDispatcher)) return;
    setDispatcherUnits([...dispatcherUnits, selectedDispatcher]);
    setSelectedDispatcher("");
  };

  // Remove dispatcher
  const handleRemoveDispatcher = (unitName) => {
    setDispatcherUnits(dispatcherUnits.filter(u => u !== unitName));
  };

  // Close roll call
  const handleCloseRollCall = async () => {
    if (!activeRollCall) return;
    try {
      // Save first
      await axios.put(`${API}/rollcall/${activeRollCall.rollcall_id}`, {
        parsha: parsha,
        notes: notes,
        dispatcher_units: dispatcherUnits
      }, { withCredentials: true });

      // Then close
      await axios.post(`${API}/rollcall/${activeRollCall.rollcall_id}/close`, {}, { withCredentials: true });
      setActiveRollCall(null);
      setParsha("");
      setNotes("");
      setDispatcherUnits([]);
      setConfirmClose(false);
      toast.success("Roll call saved to history");
    } catch (error) {
      toast.error("Failed to close roll call");
    }
  };

  // Reopen roll call
  const handleReopenRollCall = async (rollcallId) => {
    try {
      const res = await axios.post(`${API}/rollcall/${rollcallId}/reopen`, {}, { withCredentials: true });
      setActiveRollCall(res.data);
      setParsha(res.data.parsha || "");
      setNotes(res.data.notes || "");
      setDispatcherUnits(res.data.dispatcher_units || []);
      const dt = new Date(res.data.roll_call_date || res.data.created_at);
      setRollCallDate(toLocalDateTimeString(dt));
      setActiveTab("rollcall");
      setConfirmReopen(null);
      setShowViewDialog(false);
      toast.success("Roll call reopened for editing");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reopen roll call");
    }
  };

  // CSV helper function
  const escapeCSV = (value) => {
    if (value == null) return "";
    const str = String(value);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const formatDateCSV = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Download CSV helper
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Convert roll calls to CSV
  const rollCallsToCSV = (items) => {
    const headers = ["Date", "Parsha", "Created By", "Closed At", "Units", "Unit Count", "Dispatchers", "Notes"];
    const rows = items.map(rc => [
      escapeCSV(formatDateCSV(rc.roll_call_date || rc.created_at)),
      escapeCSV(rc.parsha || ""),
      escapeCSV(rc.created_by_name || ""),
      escapeCSV(rc.closed_at ? formatDateCSV(rc.closed_at) : ""),
      escapeCSV((rc.assigned_units || []).join("; ")),
      rc.assigned_units?.length || 0,
      escapeCSV((rc.dispatcher_units || []).join("; ")),
      escapeCSV(rc.notes || "")
    ]);
    return [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
  };

  // Open export dialog
  const openExportDialog = (type, options = {}) => {
    setExportType(type);
    setExportStartDate(historyStartDate || "");
    setExportEndDate(historyEndDate || "");
    setExportUnitName(options.unitName || "");
    setExportRollCallId(options.rollcallId || "");
    setMasterExportType("detailed");
    setShowExportDialog(true);
  };

  // Execute export
  const executeExport = async () => {
    try {
      const params = new URLSearchParams();
      if (exportStartDate) params.append("start_date", exportStartDate);
      if (exportEndDate) params.append("end_date", exportEndDate);

      if (exportType === "single" && exportRollCallId) {
        params.append("rollcall_id", exportRollCallId);
        const res = await axios.get(`${API}/rollcall/export?${params}`, { withCredentials: true });
        const csv = rollCallsToCSV(res.data.items);
        downloadCSV(csv, `rollcall_${exportRollCallId}.csv`);
        toast.success("Roll call exported");
      } else if (exportType === "unit" && exportUnitName) {
        params.append("unit_name", exportUnitName);
        const res = await axios.get(`${API}/rollcall/export?${params}`, { withCredentials: true });
        const csv = rollCallsToCSV(res.data.items);
        downloadCSV(csv, `rollcalls_${exportUnitName}_${new Date().toISOString().split('T')[0]}.csv`);
        toast.success(`Exported ${res.data.total} roll calls for ${exportUnitName}`);
      } else if (exportType === "master") {
        // Master export - all units across all roll calls
        const res = await axios.get(`${API}/rollcall/export?${params}`, { withCredentials: true });
        const allRollCalls = res.data.items;

        // Get unique units
        const allUnits = new Set();
        allRollCalls.forEach(rc => {
          (rc.assigned_units || []).forEach(u => allUnits.add(u));
        });
        const unitsArray = Array.from(allUnits).sort();

        if (masterExportType === "detailed") {
          // Detailed: rows are roll calls, columns are units with checkmarks
          const headers = ["Date", "Parsha", ...unitsArray];
          const rows = allRollCalls.map(rc => {
            const unitChecks = unitsArray.map(u =>
              (rc.assigned_units || []).includes(u) ? "✓" : ""
            );
            return [
              escapeCSV(formatDateCSV(rc.roll_call_date || rc.created_at)),
              escapeCSV(rc.parsha || ""),
              ...unitChecks
            ];
          });
          const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
          downloadCSV(csv, `rollcall_master_detailed_${new Date().toISOString().split('T')[0]}.csv`);
        } else {
          // Summary: just unit names and counts
          const unitCounts = {};
          unitsArray.forEach(u => unitCounts[u] = 0);
          allRollCalls.forEach(rc => {
            (rc.assigned_units || []).forEach(u => {
              if (unitCounts[u] !== undefined) unitCounts[u]++;
            });
          });
          const headers = ["Unit", "Roll Call Count"];
          const rows = unitsArray.map(u => [escapeCSV(u), unitCounts[u]]);
          const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
          downloadCSV(csv, `rollcall_master_summary_${new Date().toISOString().split('T')[0]}.csv`);
        }
        toast.success(`Master export complete (${allRollCalls.length} roll calls, ${unitsArray.length} units)`);
      } else {
        // Export all
        const res = await axios.get(`${API}/rollcall/export?${params}`, { withCredentials: true });
        const csv = rollCallsToCSV(res.data.items);
        downloadCSV(csv, `rollcalls_export_${new Date().toISOString().split('T')[0]}.csv`);
        toast.success(`Exported ${res.data.total} roll calls`);
      }
      setShowExportDialog(false);
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  // Quick export functions (open dialog)
  const handleExportAll = () => openExportDialog("all");
  const handleExportSingle = (rollcallId) => openExportDialog("single", { rollcallId });
  const handleExportUnit = (unitName) => openExportDialog("unit", { unitName });
  const handleExportMaster = () => openExportDialog("master");

  // View roll call details
  const handleViewRollCall = (rollcall) => {
    setViewingRollCall(rollcall);
    setShowViewDialog(true);
  };

  // View unit's roll call history
  const handleViewUnitHistory = (unitName) => {
    setSelectedUnitView(unitName);
    fetchUnitRollCalls(unitName);
    setShowUnitHistory(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="roll-call-page">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="rollcall" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Roll Call
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Active Roll Call */}
        <TabsContent value="rollcall" className="space-y-4">
          {!activeRollCall ? (
            <Card className="dashboard-widget">
              <CardContent className="p-6 text-center">
                <ClipboardList className="w-16 h-16 mx-auto text-slate-500 mb-4" />
                <h2 className="text-xl font-semibold mb-2">No Active Roll Call</h2>
                <p className="text-slate-400 mb-4">Start a new roll call to begin tracking attendance</p>
                <div className="flex flex-col gap-3 max-w-md mx-auto mb-4">
                  <Input
                    placeholder="This week's Parsha (optional)"
                    value={parsha}
                    onChange={(e) => setParsha(e.target.value)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="flex gap-2 justify-center">
                  <Button onClick={handleCreateRollCall} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Start Roll Call
                  </Button>
                  {parsha && (
                    <Button
                      onClick={() => setParsha("")}
                      variant="outline"
                      className="border-slate-600"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Header */}
              <Card className="dashboard-widget">
                <CardHeader className="pb-2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <ClipboardList className="w-5 h-5 text-emerald-500" />
                        Roll Call
                      </CardTitle>
                      <div className="flex items-center gap-1 mt-1">
                        {editingDate ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="datetime-local"
                              value={rollCallDate || ""}
                              onChange={(e) => {
                                if (e.target.value) {
                                  setRollCallDate(e.target.value);
                                }
                              }}
                              ref={(el) => { if (el) try { el.showPicker(); } catch {} }}
                              className="bg-slate-800 border-slate-700 h-7 text-sm w-52"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-emerald-400 hover:text-emerald-300"
                              onClick={async () => {
                                if (!rollCallDate) return;
                                try {
                                  const res = await axios.put(`${API}/rollcall/${activeRollCall.rollcall_id}`, {
                                    roll_call_date: new Date(rollCallDate).toISOString()
                                  }, { withCredentials: true });
                                  setActiveRollCall(res.data);
                                  toast.success("Date updated");
                                } catch {
                                  toast.error("Failed to update date");
                                }
                                setEditingDate(false);
                              }}
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-slate-400 hover:text-slate-300"
                              onClick={() => {
                                const dt = new Date(activeRollCall.roll_call_date || activeRollCall.created_at);
                                setRollCallDate(toLocalDateTimeString(dt));
                                setEditingDate(false);
                              }}
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-400">{formatDate(activeRollCall.roll_call_date || activeRollCall.created_at)}</span>
                            <button onClick={() => setEditingDate(true)} className="text-slate-500 hover:text-slate-300 ml-1">
                              <Pencil className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        Created {formatDate(activeRollCall.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Parsha"
                        value={parsha}
                        onChange={(e) => setParsha(e.target.value)}
                        className="bg-slate-800 border-slate-700 w-40"
                      />
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Assigned Units */}
              <Card className="dashboard-widget">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Assigned Units ({activeRollCall.assigned_units?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {sortUnitsNumerically(activeRollCall.assigned_units).map((unit) => (
                      <Badge
                        key={unit}
                        className="bg-emerald-900/50 text-emerald-300 border-emerald-700 flex items-center gap-1 px-3 py-1"
                      >
                        {unit}
                        <button
                          onClick={() => handleUnassignUnit(unit)}
                          className="ml-1 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {(!activeRollCall.assigned_units || activeRollCall.assigned_units.length === 0) && (
                      <p className="text-slate-500 text-sm">No units assigned yet</p>
                    )}
                  </div>

                  {/* Search and assign units */}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        ref={unitSearchInputRef}
                        value={unitSearch}
                        onChange={(e) => setUnitSearch(e.target.value)}
                        placeholder="Type to search and assign units..."
                        className="bg-slate-800 border-slate-700 pl-8"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-slate-600 text-slate-300 whitespace-nowrap"
                      onClick={() => setShowImportDialog(true)}
                    >
                      <ClipboardList className="w-4 h-4 mr-1" />
                      Import
                    </Button>
                  </div>
                  {unitSearch && (
                    <div className="mt-2 bg-slate-800 rounded-lg border border-slate-700 max-h-48 overflow-y-auto">
                      {availableUnits
                        .filter(u => !activeRollCall.assigned_units?.includes(u.name))
                        .filter(u => u.name.toLowerCase().includes(unitSearch.toLowerCase()))
                        .map((unit) => (
                          <div
                            key={unit.unit_id}
                            className="p-2 text-sm hover:bg-slate-700 flex items-center gap-2 cursor-pointer"
                            onMouseDown={(e) => e.preventDefault()}
                            onTouchStart={(e) => e.preventDefault()}
                            onClick={() => handleAssignUnit(unit.name)}
                          >
                            <Plus className="w-4 h-4 text-emerald-400" />
                            <span className="flex-1 font-mono">{unit.name}</span>
                          </div>
                        ))}
                      {availableUnits
                        .filter(u => !activeRollCall.assigned_units?.includes(u.name))
                        .filter(u => u.name.toLowerCase().includes(unitSearch.toLowerCase()))
                        .length === 0 && (
                        <p className="p-2 text-sm text-slate-500">No matching units</p>
                      )}
                    </div>
                  )}

                  {/* Unit History */}
                  {activeRollCall.unit_history && activeRollCall.unit_history.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <p className="text-xs text-slate-500 uppercase mb-2">Activity History</p>
                      <ScrollArea className="h-32">
                        <div className="space-y-1">
                          {activeRollCall.unit_history.slice().reverse().map((entry, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs">
                              <span className="text-slate-500">
                                {new Date(entry.timestamp).toLocaleTimeString('en-US', { timeZone: 'America/New_York' })}
                              </span>
                              <Badge
                                variant="outline"
                                className={
                                  entry.action === "assigned" ? "border-emerald-700 text-emerald-400" :
                                  entry.action === "removed" ? "border-red-700 text-red-400" :
                                  entry.action === "reopened" ? "border-amber-700 text-amber-400" :
                                  "border-slate-600 text-slate-400"
                                }
                              >
                                {entry.action}
                              </Badge>
                              {entry.unit_name && <span className="text-slate-300">{entry.unit_name}</span>}
                              <span className="text-slate-400">by {entry.performed_by_name || "Unknown"}</span>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Dispatchers */}
              <Card className="dashboard-widget">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <Radio className="w-4 h-4" />
                    Dispatchers ({dispatcherUnits.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {dispatcherUnits.map((unit) => (
                      <Badge
                        key={unit}
                        className="bg-blue-900/50 text-blue-300 border-blue-700 flex items-center gap-1 px-3 py-1"
                      >
                        {unit}
                        <button
                          onClick={() => handleRemoveDispatcher(unit)}
                          className="ml-1 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                    {dispatcherUnits.length === 0 && (
                      <p className="text-slate-500 text-sm">No dispatchers assigned</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Select value={selectedDispatcher} onValueChange={setSelectedDispatcher}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 flex-1">
                        <SelectValue placeholder="Select dispatcher" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDispatchers
                          .filter(u => !dispatcherUnits.includes(u.name))
                          .map((unit) => (
                            <SelectItem key={unit.unit_id} value={unit.name}>
                              {unit.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddDispatcher} disabled={!selectedDispatcher}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card className="dashboard-widget">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Add notes for this roll call..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="bg-slate-800 border-slate-700 min-h-[100px]"
                  />
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-2">
                <Button onClick={handleUpdateRollCall} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={() => setConfirmClose(true)} variant="outline" className="flex-1 border-emerald-700 text-emerald-400 hover:bg-emerald-900/30">
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Close & Save to History
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* Tab 2: History */}
        <TabsContent value="history" className="space-y-4">
          {/* Filters */}
          <Card className="dashboard-widget">
            <CardContent className="p-3 sm:p-4">
              <div className="space-y-3">
                {/* Row 1: Search and Parsha */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Search..."
                      value={historySearch}
                      onChange={(e) => { setHistorySearch(e.target.value); setHistoryOffset(0); }}
                      className="pl-9 bg-slate-800 border-slate-700 h-10"
                    />
                  </div>
                  <Input
                    placeholder="Parsha..."
                    value={historyParshaFilter}
                    onChange={(e) => { setHistoryParshaFilter(e.target.value); setHistoryOffset(0); }}
                    className="bg-slate-800 border-slate-700 h-10"
                  />
                </div>
                {/* Row 2: Dates */}
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={historyStartDate}
                    onChange={(e) => { setHistoryStartDate(e.target.value); setHistoryOffset(0); }}
                    className="bg-slate-800 border-slate-700 h-10"
                  />
                  <Input
                    type="date"
                    value={historyEndDate}
                    onChange={(e) => { setHistoryEndDate(e.target.value); setHistoryOffset(0); }}
                    className="bg-slate-800 border-slate-700 h-10"
                  />
                </div>
                {/* Row 3: Dispatcher and Export */}
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    value={historyDispatcherFilter}
                    onValueChange={(val) => { setHistoryDispatcherFilter(val === "all" ? "" : val); setHistoryOffset(0); }}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 h-10">
                      <SelectValue placeholder="All Dispatchers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Dispatchers</SelectItem>
                      {historyDispatchersList.map((d) => (
                        <SelectItem key={d.dispatcher_name} value={d.dispatcher_name}>
                          {d.dispatcher_name} ({d.rollcall_count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleExportAll} variant="outline" className="border-slate-600 h-10">
                    <Download className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Export All</span>
                    <span className="sm:hidden">Export</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* History List - Show first on mobile */}
          <Card className="dashboard-widget">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                <History className="w-4 h-4" />
                Roll Call History ({historyTotal})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <ScrollArea className="h-[350px] sm:h-[400px]">
                <div className="space-y-2">
                  {historyList.map((rc) => (
                    <div
                      key={rc.rollcall_id}
                      className="p-2 sm:p-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 active:border-slate-400 dark:active:border-slate-500 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-slate-500 flex-shrink-0" />
                            <span className="font-medium text-sm sm:text-base truncate">
                              {formatDate(rc.roll_call_date || rc.created_at)}
                            </span>
                            {rc.parsha && (
                              <Badge className="bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 text-xs">
                                {rc.parsha}
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                            <Users className="w-3 h-3" />
                            <span>{rc.assigned_units?.length || 0} units</span>
                            {rc.dispatcher_units?.length > 0 && (
                              <>
                                <span className="text-slate-600">|</span>
                                <Radio className="w-3 h-3" />
                                <span>{rc.dispatcher_units.length} disp</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-0 sm:gap-1 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewRollCall(rc)}
                            className="text-slate-400 hover:text-white h-8 w-8 p-0"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleExportSingle(rc.rollcall_id)}
                            className="text-slate-400 hover:text-white h-8 w-8 p-0 hidden sm:flex"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmReopen(rc.rollcall_id)}
                            className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {historyList.length === 0 && (
                    <p className="text-slate-500 text-sm text-center py-8">No roll call history found</p>
                  )}
                </div>
              </ScrollArea>

              {/* Pagination */}
              {historyTotal > 50 && (
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setHistoryOffset(Math.max(0, historyOffset - 50))}
                    disabled={historyOffset === 0}
                    className="h-8 px-2 sm:px-3"
                  >
                    Prev
                  </Button>
                  <span className="text-xs sm:text-sm text-slate-400">
                    {historyOffset + 1}-{Math.min(historyOffset + 50, historyTotal)} of {historyTotal}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setHistoryOffset(historyOffset + 50)}
                    disabled={historyOffset + 50 >= historyTotal}
                    className="h-8 px-2 sm:px-3"
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Units List - Show below history */}
          <Card className="dashboard-widget">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Units ({unitsList.length})
                </CardTitle>
                <Button
                  onClick={handleExportMaster}
                  variant="outline"
                  size="sm"
                  className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                >
                  <Download className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Master Export</span>
                  <span className="sm:hidden">Export</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {[...unitsList].sort((a, b) => {
                  // Extract numbers from unit names for numerical sorting
                  const numA = parseInt((a.unit_name.match(/\d+/) || [0])[0]);
                  const numB = parseInt((b.unit_name.match(/\d+/) || [0])[0]);
                  if (numA !== numB) return numA - numB;
                  // If numbers are same or no numbers, sort alphabetically
                  return a.unit_name.localeCompare(b.unit_name);
                }).map((unit) => (
                  <button
                    key={unit.unit_name}
                    onClick={() => handleViewUnitHistory(unit.unit_name)}
                    className="flex flex-col items-center p-2 sm:p-3 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-slate-500 active:border-blue-500 transition-colors text-center"
                  >
                    <User className="w-5 h-5 text-blue-400 mb-1" />
                    <span className="font-medium text-sm truncate w-full">{unit.unit_name}</span>
                    <span className="text-xs text-slate-500">{unit.rollcall_count} calls</span>
                  </button>
                ))}
              </div>
              {unitsList.length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">No units in roll call history</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Roll Call Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <ClipboardList className="w-5 h-5 text-emerald-500" />
              Roll Call Details
            </DialogTitle>
          </DialogHeader>
          {viewingRollCall && (
            <div className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Date</p>
                  <p className="font-medium text-sm sm:text-base">{formatDate(viewingRollCall.roll_call_date || viewingRollCall.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Parsha</p>
                  <p className="font-medium text-sm sm:text-base">{viewingRollCall.parsha || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Created</p>
                  <p className="font-medium text-sm sm:text-base">{formatDate(viewingRollCall.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Created By</p>
                  <p className="font-medium text-sm sm:text-base">{viewingRollCall.created_by_name || "Unknown"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Closed</p>
                  <p className="font-medium text-sm sm:text-base">{viewingRollCall.closed_at ? formatDate(viewingRollCall.closed_at) : "N/A"}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-slate-500 uppercase mb-2">Assigned Units ({viewingRollCall.assigned_units?.length || 0})</p>
                <div className="flex flex-wrap gap-2">
                  {sortUnitsNumerically(viewingRollCall.assigned_units).map((unit) => (
                    <Badge key={unit} className="bg-emerald-900/50 text-emerald-300 border-emerald-700 text-xs sm:text-sm px-3 py-1">
                      {unit}
                    </Badge>
                  ))}
                  {(!viewingRollCall.assigned_units || viewingRollCall.assigned_units.length === 0) && (
                    <p className="text-slate-500 text-sm">No units assigned</p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs text-slate-500 uppercase mb-2">Dispatchers ({viewingRollCall.dispatcher_units?.length || 0})</p>
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  {sortUnitsNumerically(viewingRollCall.dispatcher_units || []).map((unit) => (
                    <Badge key={unit} className="bg-blue-900/50 text-blue-300 border-blue-700 text-xs sm:text-sm">
                      {unit}
                    </Badge>
                  ))}
                  {(!viewingRollCall.dispatcher_units || viewingRollCall.dispatcher_units.length === 0) && (
                    <p className="text-slate-500 text-sm">No dispatchers</p>
                  )}
                </div>
              </div>

              {viewingRollCall.notes && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-2">Notes</p>
                  <p className="text-sm bg-slate-800 p-2 sm:p-3 rounded whitespace-pre-wrap">{viewingRollCall.notes}</p>
                </div>
              )}

              {viewingRollCall.unit_history && viewingRollCall.unit_history.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-2">Activity History</p>
                  <ScrollArea className="h-32 sm:h-40 bg-slate-800 rounded p-2">
                    <div className="space-y-1">
                      {viewingRollCall.unit_history.map((entry, idx) => (
                        <div key={idx} className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs">
                          <span className="text-slate-500">
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </span>
                          <Badge
                            variant="outline"
                            className={
                              entry.action === "assigned" ? "border-emerald-700 text-emerald-400" :
                              entry.action === "removed" ? "border-red-700 text-red-400" :
                              entry.action === "reopened" ? "border-amber-700 text-amber-400" :
                              "border-slate-600 text-slate-400"
                            }
                          >
                            {entry.action}
                          </Badge>
                          {entry.unit_name && <span className="text-slate-300">{entry.unit_name}</span>}
                          <span className="text-slate-400">by {entry.performed_by_name || "Unknown"}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 pt-3 sm:pt-4 border-t border-slate-700">
                <Button
                  onClick={() => handleExportSingle(viewingRollCall.rollcall_id)}
                  variant="outline"
                  className="flex-1 h-10"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button
                  onClick={() => setConfirmReopen(viewingRollCall.rollcall_id)}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 h-10"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reopen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unit History Dialog */}
      <Dialog open={showUnitHistory} onOpenChange={setShowUnitHistory}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <User className="w-5 h-5 text-blue-500" />
              <span className="truncate">{selectedUnitView}</span>
              <Badge variant="outline" className="text-xs ml-auto">{unitRollCallsTotal} total</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4">
            <Button
              onClick={() => handleExportUnit(selectedUnitView)}
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Unit Data
            </Button>
            <ScrollArea className="h-[350px] sm:h-[400px]">
              <div className="space-y-2">
                {unitRollCalls.map((rc) => (
                  <div
                    key={rc.rollcall_id}
                    className="p-2 sm:p-3 bg-slate-800/50 rounded-lg border border-slate-700 active:border-slate-500"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-slate-500 flex-shrink-0" />
                          <span className="font-medium text-sm sm:text-base">{formatDate(rc.roll_call_date || rc.created_at)}</span>
                          {rc.parsha && (
                            <Badge className="bg-amber-900/50 text-amber-300 border-amber-700 text-xs">
                              {rc.parsha}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs sm:text-sm text-slate-400">
                          {rc.assigned_units?.length || 0} total units
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowUnitHistory(false);
                          handleViewRollCall(rc);
                        }}
                        className="h-8 w-8 p-0 flex-shrink-0"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {unitRollCalls.length === 0 && (
                  <p className="text-slate-500 text-sm text-center py-8">No roll calls found for this unit</p>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Close Dialog */}
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close Roll Call?</AlertDialogTitle>
            <AlertDialogDescription>
              This will save the roll call to history. You can reopen it later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCloseRollCall} className="bg-emerald-600 hover:bg-emerald-700">
              Close & Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Reopen Dialog */}
      <AlertDialog open={!!confirmReopen} onOpenChange={() => setConfirmReopen(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen Roll Call?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reopen the roll call for editing. Any currently active roll call must be closed first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleReopenRollCall(confirmReopen)} className="bg-amber-600 hover:bg-amber-700">
              Reopen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Units Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(v) => { if (!importing) { setShowImportDialog(v); if (!v) setImportText(""); } }}>
        <DialogContent className="max-w-sm p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-emerald-500" />
              Import Units
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400">Paste unit numbers, one per line or separated by commas.</p>
          <Textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={"e.g.\n101\n102\n103\n\nor: 101, 102, 103"}
            className="bg-slate-800 border-slate-700 min-h-[120px] font-mono text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => { setShowImportDialog(false); setImportText(""); }} disabled={importing}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleBulkImport} disabled={importing || !importText.trim()}>
              {importing ? "Importing..." : "Assign All"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-md p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-500" />
              Export Roll Calls (CSV)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Export Type Info */}
            <div className="p-3 bg-slate-800 rounded-lg">
              <p className="text-sm text-slate-300">
                {exportType === "all" && "Export all roll calls"}
                {exportType === "single" && "Export single roll call"}
                {exportType === "unit" && `Export roll calls for: ${exportUnitName}`}
                {exportType === "master" && "Master export: All units across all roll calls"}
              </p>
            </div>

            {/* Date Range Filter */}
            <div className="space-y-2">
              <p className="text-xs text-slate-500 uppercase">Date Range (Optional)</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-400">Start Date</label>
                  <Input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                    className="bg-slate-800 border-slate-700 h-10"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">End Date</label>
                  <Input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                    className="bg-slate-800 border-slate-700 h-10"
                  />
                </div>
              </div>
            </div>

            {/* Master Export Options */}
            {exportType === "master" && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 uppercase">Export Format</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMasterExportType("detailed")}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      masterExportType === "detailed"
                        ? "border-blue-500 bg-blue-900/30"
                        : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
                    }`}
                  >
                    <p className="font-medium text-sm">Detailed</p>
                    <p className="text-xs text-slate-400 mt-1">
                      All roll calls with ✓ for each unit present
                    </p>
                  </button>
                  <button
                    onClick={() => setMasterExportType("summary")}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      masterExportType === "summary"
                        ? "border-blue-500 bg-blue-900/30"
                        : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
                    }`}
                  >
                    <p className="font-medium text-sm">Summary</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Unit names with total roll call counts
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowExportDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={executeExport}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
